import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import { StatusPill, PriorityPill } from "@/pages/HelpTickets";
import { Shield, Search } from "lucide-react";

const STATUSES = ["Open", "Awaiting customer", "Awaiting support", "Resolved", "Closed"];

export default function SuperAdminTickets() {
  const { formatDateTime } = useLocale();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/super-admin/tickets", { params }),
        api.get("/super-admin/tickets/stats"),
      ]);
      setItems(data.items || []);
      setStats(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.status]);

  return (
    <AppShell title="Ticket console">
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-semibold uppercase tracking-wider mb-1">
              <Shield className="w-3 h-3" /> Super admin
            </div>
            <h1 className="text-2xl font-semibold" data-testid="super-tickets-heading">Ticket console</h1>
            <p className="text-sm text-navy-500 mt-0.5">Triage every customer's support queue from one inbox.</p>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="super-tickets-stats">
            <StatChip label="Total" value={stats.total} />
            <StatChip label="Awaiting support" value={stats.awaiting_support} accent="bg-orange-100 text-orange-700 border-orange-200" />
            <StatChip label="Awaiting customer" value={stats.awaiting_customer} accent="bg-yellow-100 text-yellow-800 border-yellow-200" />
            <StatChip label="Resolved" value={stats.resolved} accent="bg-green-100 text-green-700 border-green-200" />
            <StatChip label="Closed" value={stats.closed} accent="bg-navy-100 text-navy-500 border-navy-200" />
          </div>
        )}

        <div className="eb-card p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="super-tickets-filters">
          <div className="flex items-center gap-1.5 text-xs">
            <button onClick={() => setFilters((f) => ({ ...f, status: "" }))} className={`px-2 py-1 rounded ${filters.status === "" ? "bg-navy-900 text-white" : "border border-navy-200"}`} data-testid="super-tickets-filter-all">All</button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilters((f) => ({ ...f, status: s }))}
                className={`px-2 py-1 rounded ${filters.status === s ? "bg-navy-900 text-white" : "border border-navy-200"}`}
                data-testid={`super-tickets-filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
          <form
            className="ml-auto flex items-center gap-2 border border-navy-200 rounded-md px-2"
            onSubmit={(e) => { e.preventDefault(); load(); }}
          >
            <Search className="w-3.5 h-3.5 text-navy-400" />
            <input
              type="search"
              placeholder="Search subject…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="text-xs py-1.5 bg-transparent outline-none w-48"
              data-testid="super-tickets-search-input"
            />
          </form>
        </div>

        {loading ? (
          <div className="text-xs text-navy-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="eb-card p-8 text-center text-sm text-navy-500" data-testid="super-tickets-empty">
            No tickets match the current filter.
          </div>
        ) : (
          <div className="eb-card p-0 overflow-hidden" data-testid="super-tickets-list">
            <table className="w-full text-sm">
              <thead className="bg-navy-50/40 text-[11px] uppercase tracking-wider text-navy-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Org</th>
                  <th className="text-left px-4 py-2.5">Subject</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Priority</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} className="border-t border-navy-100 hover:bg-navy-50/40" data-testid={`super-ticket-row-${t.id}`}>
                    <td className="px-4 py-2.5 text-xs font-medium text-navy-600">{t.org_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <Link to={`/super-admin/tickets/${t.id}`} className="font-medium text-navy-900 dark:text-white hover:text-orange-600">
                        {t.subject}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-navy-500 text-xs">{t.category}</td>
                    <td className="px-4 py-2.5"><PriorityPill priority={t.priority} /></td>
                    <td className="px-4 py-2.5"><StatusPill status={t.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-navy-500">{formatDateTime(t.last_message_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatChip({ label, value, accent }) {
  return (
    <div className={`eb-card p-3 border ${accent || ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-navy-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
